import React from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import { fixArabicText, pageUrl } from "../../../helpers";
import { i18n, navigate } from "../../../services";
import {
    Tip,
    EVT_KEYDOWN,
    KEY_DOWN_ARROW,
    KEY_ENTER,
    KEY_ESC,
    KEY_T,
    KEY_UP_ARROW,
} from "../../../lib";
import { THEME_LOW } from "../../../reducers/reader";
import { menuBlack, menuWhite } from "../../../images";

import Header from "./Header";
import { Container, Item, List, Text } from "./styles";
import { truncateTOC } from "../../../helpers/toc";
import { ReaderDoc } from "../PageContents/index";
import { AppState } from "../../../store";
import { Page } from "../../../pages/BookPage/TableOfContents";

const MenuIconContainer = styled.div`
    position: fixed;
    left: 50%;
    top: 9vh;
    margin-left: -11px;
    z-index: 30;
    cursor: pointer;
`;

const Icon = styled.img`
    width: 21px;
    height: 14px;
`;

const mapState = ({ reader }: AppState) => ({
    // @ts-ignore
    theme: reader.get("theme") as string,
});
type StateProps = ReturnType<typeof mapState>;

type OwnProps = {
    doc_id: number;
    page_id: number;
    doc: ReaderDoc;
    theme: string;
};
type Props = StateProps & OwnProps;
type State = {
    show: boolean;
    index: number;
    open: boolean;
    width: number;
};

class TableOfContents extends React.Component<Props, State> {
    private elmList: React.RefObject<HTMLDivElement> | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            open: false,
            width: 0,
            show: false,
            index: this.getIndex(props),
        };

        this.elmList = React.createRef<HTMLDivElement>();
    }

    componentDidMount() {
        window.addEventListener(EVT_KEYDOWN, this.onShortcut);
    }

    componentWillUnmount() {
        this.elmList = null;
        window.removeEventListener(EVT_KEYDOWN, this.onShortcut);
    }

    componentWillReceiveProps(nextProps: Props) {
        this.setState({
            index: this.getIndex(nextProps),
        });
    }

    close = () => this.setState({ open: false });

    onShortcut = (evt: KeyboardEvent) => {
        if (evt.key.toUpperCase() === KEY_T) {
            this.onToggle();
        } else if (this.state.open) {
            switch (evt.key) {
                case KEY_ESC:
                    this.close();
                    break;

                case KEY_UP_ARROW:
                    this.onUpArrow();
                    break;

                case KEY_DOWN_ARROW:
                    this.onDownArrow();
                    break;

                case KEY_ENTER:
                    this.close();

                    const page = this.props.doc.pages[this.state.index];
                    navigate.to(pageUrl(this.props.doc, page));
                    break;
            }

            if ([KEY_UP_ARROW, KEY_DOWN_ARROW, KEY_ENTER].includes(evt.key)) {
                evt.preventDefault();
                return false;
            }
        }
    };

    getIndex({ page_id, doc: { pages } }: Props) {
        if (isNaN(page_id)) {
            return 0;
        }

        let index = -1;
        pages.forEach((p, i) => {
            if (p.id === page_id) {
                index = i;
            }
        });

        return index;
    }

    onToggle = () =>
        this.setState(
            {
                open: !this.state.open,
                index: this.getIndex(this.props),
            },
            () => {
                setTimeout(() => this.scrollToActiveTOC(), 250);
            }
        );

    scrollToActiveTOC = () => {
        if (!this.elmList) {
            return;
        }

        const elm = document.getElementById(`toc-item-${this.activePageId()}`);
        if (elm) {
            elm.scrollIntoView();
        }
    };

    onDownArrow = () => {
        const {
            doc: { pages },
        } = this.props;
        const { index } = this.state;
        this.setState({
            index: index + 1 < pages.length ? index + 1 : 0,
        });
    };

    onUpArrow = () => {
        const {
            doc: { pages },
        } = this.props;
        const { index } = this.state;
        this.setState({
            index: index - 1 >= 0 ? index - 1 : pages.length - 1,
        });
    };

    goToPage = (p: Pick<Page, "id" | "name">) => {
        this.setState({ open: false });
        navigate.to(pageUrl(this.props.doc, p));
    };

    activePageId() {
        const { index } = this.state;

        if (index <= 0) {
            return -1;
        }

        return this.props.doc.pages[index].id;
    }

    findIndentAmount(
        list: Array<{
            _indent: number;
            id: number;
            parent_id: number;
        }>,
        parentId: number,
        indent: number
    ) {
        for (let i = 0; i < list.length; i++) {
            const item = list[i];
            if (item.id === parentId) {
                return item._indent + 1;
            }
        }

        return indent;
    }

    list() {
        const { theme, doc, page_id } = this.props;
        const activeId = this.activePageId();
        const darkTheme = theme === THEME_LOW;
        const list = truncateTOC(doc.pages) as Array<{
            _indent: number;
            id: number;
            parent_id: number;
            name: string;
            position: number;
        }>;

        list.map((i) => (i._indent = i.parent_id === 0 ? 0 : 1));

        for (let i = 0; i < list.length; i++) {
            const item = list[i];
            if (item.parent_id > 0) {
                item._indent = this.findIndentAmount(list, item.parent_id, 1);
            }
        }

        return list.map((i) => (
            <Item
                key={`toc-${i.id}`}
                id={`toc-item-${i.id}`}
                theme={theme}
                darkTheme={darkTheme}
                active={i.id === activeId}
                onClick={() => this.goToPage(i)}
                primary={isNaN(page_id) ? i.position === 1 : i.id === page_id}
            >
                <Text
                    indent={i._indent}
                    dangerouslySetInnerHTML={fixArabicText(i.name)}
                />
            </Item>
        ));
    }

    render() {
        const { theme } = this.props;
        const darkTheme = theme === THEME_LOW;

        return (
            <Container>
                <MenuIconContainer>
                    <Tip
                        anchor="left-top"
                        text={i18n.get("tip.toc")}
                        ltr={KEY_T}
                    >
                        <Icon
                            alt="فہرست ٹوگل کرو"
                            src={darkTheme ? menuWhite : menuBlack}
                            onClick={this.onToggle}
                        />
                    </Tip>
                </MenuIconContainer>
                <List open={this.state.open} theme={theme} ref={this.elmList}>
                    <Header {...this.props} />
                    {this.list()}
                </List>
            </Container>
        );
    }
}

export default connect(mapState)(TableOfContents);
